const BLOG_API_BASE_URL = 'https://apiclientalio.azurewebsites.net';

function buildUrl(path, queryParams = {}) {
  const url = new URL(path, BLOG_API_BASE_URL);

  Object.entries(queryParams).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      url.searchParams.set(key, value);
    }
  });

  return url.toString();
}

async function requestJson(path, options = {}) {
  const { queryParams, method = 'GET' } = options;
  const response = await fetch(buildUrl(path, queryParams), {
    method,
    headers: {
      Accept: 'application/json; x-api-version=1.0, application/json'
    }
  });

  if (!response.ok) {
    throw new Error(`Blog API request failed with status ${response.status}`);
  }

  return response.json();
}

function parseJsonish(value) {
  if (typeof value !== 'string') {
    return value;
  }

  try {
    return JSON.parse(value);
  } catch (error) {
    return value;
  }
}

function unwrapList(payload) {
  const parsedPayload = parseJsonish(payload);

  if (Array.isArray(parsedPayload)) {
    return parsedPayload;
  }

  const parsedData = parseJsonish(parsedPayload?.data);
  const candidates = [
    parsedData,
    parsedData?.items,
    parsedData?.blogs,
    parsedData?.records,
    parsedData?.data,
    parsedPayload?.result,
    parsedPayload?.items,
    parsedPayload?.blogs,
    parsedPayload?.records
  ];

  return candidates.find(Array.isArray) || [];
}

function unwrapItem(payload) {
  const parsedPayload = parseJsonish(payload);

  if (!parsedPayload || Array.isArray(parsedPayload)) {
    return parsedPayload;
  }

  const parsedData = parseJsonish(parsedPayload.data);

  return parsedData || parsedPayload.result || parsedPayload.blog || parsedPayload.item || parsedPayload;
}

function stripHtml(value) {
  return String(value || '').replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
}

function normalizeTerms(value) {
  if (!value) {
    return [];
  }

  if (Array.isArray(value)) {
    return value
      .map((item) => {
        if (typeof item === 'string') {
          return item;
        }

        return item?.name || item?.title || item?.tagName || item?.categoryName || item?.slug;
      })
      .filter(Boolean);
  }

  if (typeof value === 'string') {
    return value.split(',').map((item) => item.trim()).filter(Boolean);
  }

  return [];
}

function slugify(value) {
  return String(value || 'blog')
    .trim()
    .toLowerCase()
    .replace(/&/g, 'and')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '') || 'blog';
}

function normalizeContentType(blog) {
  const contentType = blog.contentType || blog.contentTypeName || blog.type || blog.categoryName || blog.category?.name || blog.category || 'blog';

  if (typeof contentType === 'string') {
    return contentType;
  }

  return contentType?.name || contentType?.title || contentType?.slug || 'blog';
}

export function normalizeBlog(blog) {
  if (!blog) {
    return null;
  }

  const content = blog.blogContent || blog.content || blog.htmlContent || blog.body || blog.description || '';
  const plainText = stripHtml(content);
  const category = blog.categoryName || blog.category?.name || blog.category || '';
  const contentType = normalizeContentType(blog);
  const title = blog.title || blog.name || 'Untitled blog';
  const featuredImage = blog.featuredImage || blog.imageUrl || blog.featuredImageUrl || blog.thumbnailUrl || blog.coverImageUrl || '';
  const publishedAt = blog.publishedOn || blog.publishedAt || blog.publishDate || blog.createdOn || blog.createdAt || blog.updatedOn || blog.updatedAt || '';

  return {
    id: blog.id || blog.blogId || blog._id || '',
    blogId: blog.blogId || blog.id || '',
    categoryId: blog.blogCategoryId || blog.categoryId || '',
    slug: blog.slug || blog.seoSlug || blog.urlSlug || blog.id || blog.blogId || '',
    title,
    excerpt: blog.excerpt || blog.summary || blog.shortDescription || plainText.slice(0, 180),
    content,
    category: typeof category === 'string' ? category : category?.name || '',
    contentType,
    contentTypeSlug: blog.contentTypeSlug || blog.typeSlug || slugify(contentType),
    tags: normalizeTerms(blog.tags || blog.blogTags || blog.tagNames),
    author: blog.authorName || blog.author?.name || blog.createdBy || '',
    authorEmployeeId: blog.authorEmployeeId || blog.author?.id || '',
    imageUrl: featuredImage,
    featuredImage,
    bannerImage: blog.bannerImage || blog.bannerImageUrl || '',
    readingTime: blog.readingTime || blog.readTime || '',
    publishedAt,
    createdAt: blog.createdOn || blog.createdAt || '',
    updatedAt: blog.updatedOn || blog.updatedAt || '',
    viewCount: blog.viewCount ?? '',
    isFeatured: Boolean(blog.isFeatured),
    allowComments: Boolean(blog.allowComments),
    status: blog.status ?? '',
    metaTitle: blog.metaTitle || blog.seoTitle || title,
    metaDescription: blog.metaDescription || blog.seoDescription || blog.excerpt || blog.summary || plainText.slice(0, 155),
    metaKeywords: blog.metaKeywords || '',
    canonicalUrl: blog.canonicalUrl || ''
  };
}

export async function getPublishedBlogs(options = {}) {
  const payload = await requestJson('/api/v1/blog/blog-list', {
    method: 'POST',
    queryParams: {
      search: options.search,
      status: options.status,
      categoryId: options.categoryId,
      isFeatured: options.isFeatured,
      page: options.page ?? 1,
      pageSize: options.pageSize ?? 100
    }
  });

  return unwrapList(payload).map(normalizeBlog).filter(Boolean);
}

export async function getBlogBySlug(slug) {
  const payload = await requestJson(`/api/v1/blog/blogbyslug/${encodeURIComponent(slug)}`);
  return normalizeBlog(unwrapItem(payload));
}

export async function getBlogById(id) {
  const payload = await requestJson(`/api/v1/blog/blogbyId/${encodeURIComponent(id)}`);
  return normalizeBlog(unwrapItem(payload));
}

export async function getContentTypes() {
  const payload = await requestJson('/api/v1/blog/contentTypes');
  return normalizeTerms(unwrapList(payload));
}

export const getBlogCategories = getContentTypes;

export async function getBlogTags() {
  return [];
}

export { slugify };